import { isString } from '@resi-media/resi-ui';

const RETURN_TYPE = {
  FULFILLED: 'fulfilled',
  REJECTED: 'rejected',
} as const;

type MapStatus = (typeof RETURN_TYPE)[keyof typeof RETURN_TYPE];

type MapReturn<T> = (
  | { reason: unknown; requestId: string; status: MapStatus; value?: undefined }
  | { reason?: undefined; requestId: string; status: MapStatus; value: T }
)[];

type OptionalNestedParam<U> = U extends string[] ? string[] : string[][];
type OptionalParam<U> = U extends string[] ? string : string[];
type MapFn<T, U> = (currentValue: OptionalParam<U>, index: number, array: OptionalNestedParam<U>) => Promise<T>;
type GetId<U> = (value: OptionalParam<U>, index: number) => string;

const defaultGetId = <U>(v: OptionalParam<U>, i: number) =>
  isString(v) ? v : Array.isArray(v) && isString(v[0]) ? v[0] : `item-${i}`;

const arrayGenerator = function* <U>(array: OptionalNestedParam<U>, signal?: AbortSignal) {
  for (let index = 0; index < array.length; index++) {
    // Checked per item rather than only up front: workers pull from this generator one at a time,
    // so aborting mid-batch stops the remaining items from ever being dequeued.
    if (signal?.aborted) return;
    yield { currentValue: array[index] as OptionalParam<U>, index, array };
  }
};

const mapItem = async <T, U>(
  mapFn: MapFn<T, U>,
  getId: GetId<U>,
  currentValue: OptionalParam<U>,
  index: number,
  array: OptionalNestedParam<U>
) => {
  const requestId = getId(currentValue, index);
  try {
    const value = await mapFn(currentValue, index, array);
    return { requestId, status: RETURN_TYPE.FULFILLED, value } as const;
  } catch (reason) {
    console.error(`[${requestId}] - failed due to reason: ${reason}`);
    return { requestId, status: RETURN_TYPE.REJECTED, reason } as const;
  }
};

const worker = async <T, U>(
  id: number,
  gen: Generator<{ array: OptionalNestedParam<U>; currentValue: OptionalParam<U>; index: number }, void, unknown>,
  mapFn: MapFn<T, U>,
  getId: GetId<U>,
  result: MapReturn<T>
) => {
  for (const { array, currentValue, index } of gen) {
    result[index] = await mapItem<T, U>(mapFn, getId, currentValue, index, array);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function asyncMap<T, U extends Array<any> = string[]>(
  arr: OptionalNestedParam<U>,
  mapFn: MapFn<T, U>,
  limit = arr.length,
  getId: GetId<U> = defaultGetId,
  /** Optional so existing callers are unaffected. When aborted, queued items are never started. */
  options?: { signal?: AbortSignal }
): Promise<MapReturn<T>> {
  if (arr.length === 0) return [];
  const result: MapReturn<T> = new Array(arr.length);
  const gen = arrayGenerator<U>(arr, options?.signal);

  // Clamp and guard against 0
  limit = Math.max(1, Math.min(limit, arr.length));

  const workers = Array.from({ length: limit }, (_, i) => worker<T, U>(i, gen, mapFn, getId, result));
  await Promise.all(workers);

  // `new Array(n)` is sparse, and an abort leaves the un-dequeued slots empty. Fill them so
  // consumers that iterate with forEach/for..of can't read `undefined` where they expect an entry.
  if (options?.signal?.aborted) {
    for (let index = 0; index < result.length; index++) {
      result[index] ??= {
        reason: new DOMException('Aborted', 'AbortError'),
        requestId: getId(arr[index] as OptionalParam<U>, index),
        status: RETURN_TYPE.REJECTED,
      };
    }
  }
  return result;
}

async function asyncMapChunk<T>(
  arr: string[][],
  mapFn: MapFn<T, string[][]>,
  limit = arr.length,
  getId?: GetId<string[][]>
): Promise<MapReturn<T>> {
  return asyncMap<T, string[][]>(arr, mapFn, limit, getId);
}

export { asyncMap, asyncMapChunk, RETURN_TYPE };
export type { MapReturn, MapStatus };
